import { useState, useEffect, useMemo } from 'react';
import { ArrowRight, ArrowLeft, Loader2, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import UAEPlate, { hasPlateDesign } from './plates/UAEPlate';
import DigitMaskPicker from './DigitMaskPicker';
import PriceModePicker from './PriceModePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AddPlatePage({ language, isDarkMode, store, onBack, onSuccess, editItem = null }) {
  const isEdit = !!editItem;
  const [emiratesData, setEmiratesData] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(true);
  const [saving, setSaving] = useState(false);

  const [emirate, setEmirate] = useState(editItem?.emirate || '');
  const [category, setCategory] = useState(editItem?.plateType || '');
  const [codeId, setCodeId] = useState(editItem?.plate_code_id || '');
  const [code, setCode] = useState(editItem?.plateCode || '');
  const [number, setNumber] = useState(editItem?.plateNumber || '');
  const [hiddenDigits, setHiddenDigits] = useState(editItem?.hiddenDigits || []);
  const [price, setPrice] = useState(editItem?.price || '');
  const [priceOnRequest, setPriceOnRequest] = useState(!!editItem?.priceOnRequest);
  const [negotiable, setNegotiable] = useState(editItem?.negotiable || false);
  const [description, setDescription] = useState(editItem?.description || '');

  const cardClass = `rounded-2xl border p-5 ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-gray-200'}`;
  const labelClass = `block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;
  const BackIcon = language === 'ar' ? ArrowRight : ArrowLeft;

  // الأكواد تُقرأ حية من لوحة تحكم ASB606 عند كل فتح — لا تخزين محلي
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/public/plate-codes/active`);
        setEmiratesData(res.data?.emirates || []);
      } catch {
        toast.error(language === 'ar' ? 'تعذر تحميل أكواد اللوحات' : 'Failed to load plate codes');
      } finally {
        setLoadingCodes(false);
      }
    })();
  }, [language]);

  const selectedEmirate = useMemo(() => emiratesData.find(e => e.id === emirate), [emiratesData, emirate]);
  const categories = selectedEmirate?.categories || [];
  const selectedCategory = categories.find(c => c.id === category);
  const codes = selectedCategory?.codes || [];
  const selectedCode = codes.find(c => c.id === codeId);
  const showOfficialPlate = emirate && category && hasPlateDesign(emirate, category);
  // قاعدة show_on_plate: الكود التصنيفي (مثل "أبيض") لا يُرسم على اللوحة أبداً
  const codeDrawnOnPlate = selectedCode
    ? (selectedCode.show_on_plate === false ? '' : selectedCode.code)
    : (editItem?.show_on_plate === false ? '' : code);

  const pickEmirate = (id) => {
    setEmirate(id);
    setCategory('');
    setCodeId('');
    setCode('');
  };
  const pickCategory = (id) => {
    setCategory(id);
    setCodeId('');
    setCode('');
  };
  const pickCode = (c) => {
    setCodeId(c.id);
    setCode(c.code);
  };

  const handleNumberChange = (v) => {
    const next = v.replace(/\D/g, '').slice(0, 5);
    setNumber(next);
    // Drop any hidden index that falls outside the new length
    setHiddenDigits(prev => prev.filter(i => i < next.length));
  };

  const handleSubmit = async () => {
    if (!emirate) { toast.error(language === 'ar' ? 'يرجى اختيار الإمارة' : 'Please select emirate'); return; }
    if (!category) { toast.error(language === 'ar' ? 'يرجى اختيار الفئة' : 'Please select category'); return; }
    if (!codeId || !code) { toast.error(language === 'ar' ? 'يرجى اختيار الكود' : 'Please select code'); return; }
    if (!/^[0-9]{1,5}$/.test(number)) { toast.error(language === 'ar' ? 'رقم اللوحة: أرقام فقط (1-5 خانات)' : 'Plate number: digits only (1-5)'); return; }
    if (!priceOnRequest && (!price || parseFloat(price) <= 0)) { toast.error(language === 'ar' ? 'يرجى إدخال سعر صحيح أو اختيار "اتصل لمعرفة السعر"' : 'Please enter a valid price or choose "Call for price"'); return; }

    setSaving(true);
    try {
      // إعادة التحقق أن الكود ما زال مفعّلاً (حماية من أكواد أوقفها الأدمن)
      const check = await axios.get(`${BACKEND_URL}/api/public/plate-codes/active?emirate=${emirate}&category=${category}`);
      const liveCodes = check.data?.emirates?.[0]?.categories?.[0]?.codes || [];
      if (!liveCodes.some(c => c.id === codeId)) {
        toast.error(language === 'ar' ? 'هذا الكود لم يعد متاحاً — اختر كوداً آخر' : 'This code is no longer available');
        setSaving(false);
        return;
      }

      const token = localStorage.getItem('business_token') || localStorage.getItem('token');
      const fd = new FormData();
      fd.append('storeType', 'car_plates');
      fd.append('plateNumber', number);
      // iter-212: digit mask (positions relative to plateNumber string)
      fd.append('hiddenDigits', JSON.stringify(hiddenDigits));
      fd.append('plateCode', code);
      fd.append('plate_code_id', codeId);
      fd.append('code_ar', selectedCode?.code_ar || code);
      fd.append('show_on_plate', selectedCode?.show_on_plate === false ? 'false' : 'true');
      fd.append('emirate', emirate);
      fd.append('plateType', category);
      fd.append('negotiable', negotiable ? 'true' : 'false');
      fd.append('price', priceOnRequest ? 0 : price);
      fd.append('priceOnRequest', priceOnRequest ? 'true' : 'false');
      if (description) fd.append('description', description);
      if (!isEdit) fd.append('status', 'active');

      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      if (isEdit) {
        await axios.put(`${BACKEND_URL}/api/stores/${store.id}/items/${editItem.id}`, fd, cfg);
        toast.success(language === 'ar' ? 'تم تحديث اللوحة بنجاح' : 'Plate updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/stores/${store.id}/items`, fd, cfg);
        toast.success(language === 'ar' ? 'تم إضافة اللوحة بنجاح' : 'Plate added successfully');
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'حدث خطأ أثناء الحفظ' : 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = emirate && category && codeId && /^[0-9]{1,5}$/.test(number) && (priceOnRequest || (price && parseFloat(price) > 0));

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-20 border-b ${isDarkMode ? 'bg-[#0F172A]/95 border-white/10' : 'bg-white/95 border-gray-200'} backdrop-blur`}>
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={onBack} className={`p-2 rounded-xl ${isDarkMode ? 'hover:bg-white/10 text-white' : 'hover:bg-gray-100'}`} data-testid="add-plate-back-btn">
            <BackIcon className="h-5 w-5" />
          </button>
          <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
            {isEdit ? (language === 'ar' ? 'تعديل اللوحة' : 'Edit Plate') : (language === 'ar' ? 'إضافة لوحة جديدة' : 'Add New Plate')}
          </h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        {/* Live Plate Preview */}
        <div className={cardClass} data-testid="plate-preview-card">
          <label className={labelClass}>{language === 'ar' ? 'معاينة اللوحة' : 'Plate Preview'}</label>
          <div className="flex justify-center py-3">
            {showOfficialPlate ? (
              <UAEPlate emirate={emirate} category={category} code={code} number={number} width={340} />
            ) : selectedCode?.image_url ? (
              <img src={selectedCode.image_url} alt="plate" className="max-h-28 rounded-lg" />
            ) : (
              <div className={`w-full max-w-[340px] h-20 rounded-lg border-2 border-dashed flex items-center justify-center text-sm ${isDarkMode ? 'border-white/20 text-gray-500' : 'border-gray-300 text-gray-400'}`}>
                {emirate && category
                  ? (language === 'ar' ? `${code || '—'} • ${number || '—'}` : `${code || '—'} • ${number || '—'}`)
                  : (language === 'ar' ? 'اختر الإمارة والفئة لعرض اللوحة' : 'Select emirate & category to preview')}
              </div>
            )}
          </div>
        </div>

        {/* Emirate */}
        <div className={cardClass}>
          <label className={labelClass}>
            <MapPin className="inline h-4 w-4 me-1" />
            {language === 'ar' ? 'الإمارة' : 'Emirate'} <span className="text-red-500">*</span>
          </label>
          {loadingCodes ? (
            <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-[#1E9BD9]" /></div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {emiratesData.map(e => (
                <button
                  key={e.id}
                  onClick={() => pickEmirate(e.id)}
                  className={`px-3 py-2.5 rounded-xl border text-sm font-medium flex items-center gap-2 transition-colors ${
                    emirate === e.id
                      ? 'border-[#1E9BD9] bg-[#1E9BD9]/10 text-[#1E9BD9]'
                      : isDarkMode ? 'border-white/10 text-gray-300 hover:border-white/30' : 'border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                  data-testid={`plate-emirate-${e.id}`}
                >
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: e.color }} />
                  {language === 'ar' ? e.name_ar : e.name_en}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Category */}
        {emirate && (
          <div className={cardClass}>
            <label className={labelClass}>{language === 'ar' ? 'الفئة' : 'Category'} <span className="text-red-500">*</span></label>
            {categories.length === 0 ? (
              <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{language === 'ar' ? 'لا توجد فئات مفعّلة لهذه الإمارة' : 'No active categories for this emirate'}</p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {categories.map(c => (
                  <button
                    key={c.id}
                    onClick={() => pickCategory(c.id)}
                    className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                      category === c.id
                        ? 'border-[#1E9BD9] bg-[#1E9BD9]/10 text-[#1E9BD9]'
                        : isDarkMode ? 'border-white/10 text-gray-300 hover:border-white/30' : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                    data-testid={`plate-category-${c.id}`}
                  >
                    {language === 'ar' ? c.name_ar : c.name_en}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Code — dropdown from ASB606, no manual input */}
        {emirate && category && (
          <div className={cardClass}>
            <label className={labelClass}>{language === 'ar' ? 'الكود' : 'Code'} <span className="text-red-500">*</span></label>
            {codes.length === 0 ? (
              <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {language === 'ar' ? 'لا توجد أكواد متاحة حالياً لهذه الفئة' : 'No codes available for this category'}
              </p>
            ) : (
              <select
                value={codeId}
                onChange={(e) => {
                  const c = codes.find(x => x.id === e.target.value);
                  if (c) pickCode(c);
                }}
                className={`w-full h-12 px-4 rounded-xl border text-base font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-[#1E9BD9]/30 ${
                  isDarkMode
                    ? 'bg-[#0F172A] border-white/10 text-white'
                    : 'bg-white border-gray-200 text-gray-900'
                }`}
                data-testid="plate-code-select"
              >
                <option value="">
                  {language === 'ar' ? '— اختر الكود —' : '— Select code —'}
                </option>
                {codes.map(c => (
                  <option key={c.id} value={c.id}>
                    {language === 'ar' ? (c.code_ar || c.code) : c.code}
                    {(c.label_ar || c.label_en) ? ` — ${language === 'ar' ? (c.label_ar || c.label_en) : (c.label_en || c.label_ar)}` : ''}
                  </option>
                ))}
              </select>
            )}
            <p className={`text-xs mt-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {language === 'ar' ? 'الأكواد تُدار من الإدارة — لا يمكن إدخال كود يدوياً' : 'Codes are managed by admin — manual entry not allowed'}
            </p>
          </div>
        )}

        {/* Plate Number — the only user-typed field */}
        <div className={cardClass}>
          <label className={labelClass}>{language === 'ar' ? 'رقم اللوحة' : 'Plate Number'} <span className="text-red-500">*</span></label>
          <input
            type="tel" inputMode="numeric"
            dir="ltr"
            value={number}
            onChange={(e) => handleNumberChange(e.target.value)}
            placeholder="12345"
            className={`w-full h-16 px-4 rounded-xl border text-3xl font-bold text-center tracking-[0.4em] ${
              isDarkMode ? 'bg-[#0F172A] border-white/10 text-white placeholder:text-gray-600' : 'bg-white border-gray-200 text-gray-900'
            }`}
            data-testid="plate-number-input"
          />
          <p className={`text-xs mt-2 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {language === 'ar' ? `أرقام فقط، من 1 إلى 5 خانات (${number.length}/5)` : `Digits only, 1-5 (${number.length}/5)`}
          </p>

          {/* iter-212: digit mask picker (max 2 hidden for plates) */}
          <DigitMaskPicker
            value={number}
            hiddenIndices={hiddenDigits}
            onChange={setHiddenDigits}
            kind="plate"
            language={language}
            isDarkMode={isDarkMode}
          />
        </div>

        {/* Price */}
        <div className={cardClass}>
          <label className={labelClass}>{language === 'ar' ? 'السعر' : 'Price'} <span className="text-red-500">*</span></label>

          <PriceModePicker
            value={priceOnRequest ? 'onRequest' : 'fixed'}
            onChange={(m) => setPriceOnRequest(m === 'onRequest')}
            language={language}
            isDarkMode={isDarkMode}
            testIdPrefix="plate"
          />

          {!priceOnRequest && (
            <div className="relative mt-3">
              <input
                type="number" inputMode="decimal"
                min="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="50000"
                className={`w-full px-4 py-3 pe-16 rounded-xl border ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                data-testid="plate-price-input"
              />
              <span className={`absolute end-4 top-1/2 -translate-y-1/2 text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>AED</span>
            </div>
          )}
          {/* Negotiable — allowed with any price mode (including "Contact for price"). */}
          <div className="flex items-center justify-between mt-4" dir="ltr">
            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {language === 'ar' ? 'قابل للتفاوض' : 'Negotiable'}
            </span>
            <button
              type="button"
              onClick={() => setNegotiable(!negotiable)}
              className={`w-12 h-6 rounded-full transition-colors relative flex-shrink-0 ${negotiable ? 'bg-[#1E9BD9]' : isDarkMode ? 'bg-white/20' : 'bg-gray-300'}`}
              data-testid="plate-negotiable-toggle"
            >
              <span className={`absolute top-0.5 h-5 w-5 bg-white rounded-full transition-all ${negotiable ? 'left-6' : 'left-0.5'}`} />
            </button>
          </div>
        </div>

        {/* Notes */}
        <div className={cardClass}>
          <label className={labelClass}>{language === 'ar' ? 'ملاحظات (اختياري)' : 'Notes (Optional)'}</label>
          <textarea
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={language === 'ar' ? 'أضف أي ملاحظات إضافية...' : 'Add any additional notes...'}
            className={`w-full px-4 py-3 rounded-xl border resize-none ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white placeholder:text-gray-600' : 'bg-white border-gray-200 text-gray-900'}`}
            data-testid="plate-description-input"
          />
        </div>

        {/* Submit — inline at end of form (not fixed) */}
        <div className="pt-2">
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || saving}
            className="w-full py-6 text-base bg-[#1E9BD9] hover:bg-[#1E9BD9]/90 disabled:opacity-50 rounded-xl font-bold"
            data-testid="plate-submit-btn"
          >
            {saving ? (
              <><Loader2 className="h-5 w-5 me-2 animate-spin" />{language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}</>
            ) : (
              isEdit ? (language === 'ar' ? 'حفظ التعديلات' : 'Save Changes') : (language === 'ar' ? 'إضافة اللوحة' : 'Add Plate')
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
